import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, ChevronDown, User, LogOut, Settings, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { services } from "@/data/services";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

const navLinks = [
  { name: "About", href: "/about" },
  { name: "Properties", href: "/properties" },
  { name: "Insights", href: "/insights" },
  { name: "Contact", href: "/contact" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-gold"
      >
        Skip to main content
      </a>
      
      <header 
        role="banner"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-background shadow-lg border-b border-border" 
            : "bg-background/95 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col">
                <span className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
                  Brant Booker
                </span>
                <span className="text-[9px] sm:text-[10px] tracking-[0.12em] sm:tracking-[0.15em] text-muted-foreground uppercase">
                  Jameson Sotheby's International Realty
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
              {navLinks.slice(0, 2).map((link) => (
                link.href.startsWith('/') && !link.href.includes('#') ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors duration-200 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors duration-200 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                  >
                    {link.name}
                  </a>
                )
              ))}
              
              {/* Services Mega Menu */}
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-transparent data-[state=open]:bg-transparent h-auto p-0 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left">
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <motion.div 
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="w-[600px] p-6 bg-background"
                      >
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                          <div>
                            <h3 className="font-display text-lg font-semibold text-foreground">Our Services</h3>
                            <p className="text-sm text-muted-foreground">Expert guidance for every real estate need</p>
                          </div>
                          <Link 
                            to="/services" 
                            className="group/link flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            View All
                            <ArrowRight className="h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                          </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {services.map((service, index) => (
                            <NavigationMenuLink key={service.slug} asChild>
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, delay: index * 0.05 }}
                              >
                                <Link
                                  to={`/services/${service.slug}`}
                                  className={cn(
                                    "group flex items-start gap-3 rounded-lg p-3 transition-all duration-200",
                                    "hover:bg-muted hover:shadow-sm hover:scale-[1.02]"
                                  )}
                                >
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-all duration-200 group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110 group-hover:shadow-md">
                                    <service.icon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                                      {service.title}
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 transition-colors group-hover:text-muted-foreground/80">
                                      {service.shortDescription}
                                    </p>
                                  </div>
                                </Link>
                              </motion.div>
                            </NavigationMenuLink>
                          ))}
                        </div>
                      </motion.div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              {navLinks.slice(2).map((link) => (
                link.href.startsWith('/') && !link.href.includes('#') ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors duration-200 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors duration-200 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                  >
                    {link.name}
                  </a>
                )
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a href="tel:+17734901862" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Phone className="h-4 w-4" />
                (773) 490-1862
              </a>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <User className="h-4 w-4" />
                      Account
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isAdmin && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2">
                            <Settings className="h-4 w-4" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2">
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="navy" size="lg">
                  Schedule Consultation
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -mr-2 text-foreground"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Slide-out Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-background z-50 lg:hidden shadow-2xl"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex flex-col">
                    <span className="font-display text-lg font-semibold text-foreground">
                      Brant Booker
                    </span>
                    <span className="text-[9px] tracking-[0.12em] text-muted-foreground uppercase">
                      Sotheby's International Realty
                    </span>
                  </div>
                  <button
                    className="p-2 -mr-2 text-foreground"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close menu"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 overflow-y-auto py-6" aria-label="Mobile navigation">
                  <div className="flex flex-col">
                    {navLinks.slice(0, 2).map((link, index) => (
                      link.href.startsWith('/') && !link.href.includes('#') ? (
                        <motion.div
                          key={link.name}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Link
                            to={link.href}
                            className="block px-6 py-4 text-lg font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/50"
                            onClick={() => setIsOpen(false)}
                          >
                            {link.name}
                          </Link>
                        </motion.div>
                      ) : (
                        <motion.a
                          key={link.name}
                          href={link.href}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="px-6 py-4 text-lg font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/50"
                          onClick={() => setIsOpen(false)}
                        >
                          {link.name}
                        </motion.a>
                      )
                    ))}
                    
                    {/* Mobile Services Accordion */}
                    <motion.button
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="px-6 py-4 text-lg font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/50 flex items-center justify-between"
                      onClick={() => setServicesExpanded(!servicesExpanded)}
                    >
                      Services
                      <ChevronDown className={`h-5 w-5 transition-transform ${servicesExpanded ? 'rotate-180' : ''}`} />
                    </motion.button>
                    
                    <AnimatePresence>
                      {servicesExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden bg-muted/30"
                        >
                          <Link
                            to="/services"
                            className="block px-8 py-3 text-base font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/30"
                            onClick={() => setIsOpen(false)}
                          >
                            All Services
                          </Link>
                          {services.map((service) => (
                            <Link
                              key={service.slug}
                              to={`/services/${service.slug}`}
                              className="flex items-center gap-3 px-8 py-3 text-base text-foreground hover:bg-muted/50 transition-colors border-b border-border/30"
                              onClick={() => setIsOpen(false)}
                            >
                              <service.icon className="h-4 w-4 text-accent" />
                              {service.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {navLinks.slice(2).map((link, index) => (
                      link.href.startsWith('/') && !link.href.includes('#') ? (
                        <motion.div
                          key={link.name}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (index + 3) * 0.05 }}
                        >
                          <Link
                            to={link.href}
                            className="block px-6 py-4 text-lg font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/50"
                            onClick={() => setIsOpen(false)}
                          >
                            {link.name}
                          </Link>
                        </motion.div>
                      ) : (
                        <motion.a
                          key={link.name}
                          href={link.href}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (index + 3) * 0.05 }}
                          className="px-6 py-4 text-lg font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border/50"
                          onClick={() => setIsOpen(false)}
                        >
                          {link.name}
                        </motion.a>
                      )
                    ))}
                  </div>
                </nav>

                {/* Footer with Contact Info */}
                <div className="p-6 border-t border-border bg-muted/30">
                  <div className="space-y-4">
                    <a 
                      href="tel:+17734901862" 
                      className="flex items-center gap-3 text-foreground hover:text-gold transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Phone className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Call Brant</div>
                        <div className="font-medium">(773) 490-1862</div>
                      </div>
                    </a>
                    <a 
                      href="mailto:bbooker@jamesonsir.com" 
                      className="flex items-center gap-3 text-foreground hover:text-gold transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Email</div>
                        <div className="font-medium text-sm">bbooker@jamesonsir.com</div>
                      </div>
                    </a>
                    {user ? (
                      <div className="space-y-2 mt-4">
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setIsOpen(false)}>
                            <Button variant="outline" className="w-full gap-2">
                              <Settings className="h-4 w-4" />
                              Admin Dashboard
                            </Button>
                          </Link>
                        )}
                        <Button variant="navy" className="w-full gap-2" onClick={handleSignOut}>
                          <LogOut className="h-4 w-4" />
                          Sign Out
                        </Button>
                      </div>
                    ) : (
                      <Button variant="navy" className="w-full mt-4" size="lg">
                        Schedule Consultation
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
